import { create } from 'zustand';
import type {
  PresentationItem,
  PresentationDeck,
  PresentationSlide,
  ContentImage,
} from '@/types/presentation';
import { parseDeck, DEFAULT_SECTIONS } from '@/lib/presentation';

export interface AIMessage {
  role: 'assistant' | 'user';
  content: string;
}

export const THEME_PRESETS = [
  { name: 'Executive Modern', primary: '#0C1B33', accent: '#C9A84C' },
  { name: 'Corporate Blue', primary: '#1D3557', accent: '#E63946' },
  { name: 'Dark Premium', primary: '#0F172A', accent: '#6366F1' },
  { name: 'Gold Luxury', primary: '#1A1A2E', accent: '#D4AF37' },
  { name: 'Vibrant Orange', primary: '#1D1D2E', accent: '#F97316' },
] as const;

export const PRESENTATION_EVENT_TYPES = [
  'Corporate Event',
  'Social Gala',
  'Wedding',
  'Product Launch',
  'Conference',
  'Anniversary',
  'Birthday',
  'Other',
];

const JOURNEY_SECTIONS = ['journey del evento', 'minuto a minuto'];
const COSTS_SECTIONS = ['cierre'];

function sectionLayout(section: string, isFirst: boolean): PresentationSlide['layout'] {
  if (isFirst) return 'cover';
  const low = section.toLowerCase();
  if (JOURNEY_SECTIONS.some((s) => low.includes(s))) return 'journey';
  if (COSTS_SECTIONS.some((s) => low.includes(s))) return 'costs';
  return 'content';
}

function makeBlankSlide(section: string, isFirst = false): PresentationSlide {
  return {
    section,
    title: section,
    subtitle: '',
    layout: sectionLayout(section, isFirst),
    bullets: isFirst ? [] : [''],
    speaker_notes: '',
    image_prompt: '',
    content_images: [],
  };
}

function createInitialDeck(): PresentationDeck {
  return {
    deck_title: '',
    theme: {
      primary_color: THEME_PRESETS[0].primary,
      accent_color: THEME_PRESETS[0].accent,
      font_title: 'Inter',
      font_body: 'Inter',
    },
    slides: DEFAULT_SECTIONS.map((s, i) => makeBlankSlide(s, i === 0)),
  };
}

export interface PresentationFields {
  name: string;
  eventType: string;
  eventDate: string;
  eventTime: string;
  location: string;
  audience: string;
  theme: string;
  primaryColor: string;
  accentColor: string;
  specialNotes: string;
  extraSections: string;
}

const DEFAULT_FIELDS: PresentationFields = {
  name: '',
  eventType: 'Corporate Event',
  eventDate: '',
  eventTime: '',
  location: '',
  audience: '',
  theme: 'Executive Modern',
  primaryColor: '#0C1B33',
  accentColor: '#C9A84C',
  specialNotes: '',
  extraSections: '',
};

interface PresentationEditorState extends PresentationFields {
  editingId: string | null;
  isDirty: boolean;
  deck: PresentationDeck;
  currentSlideIndex: number;
  logoUrl: string | null;
  venueImages: string[];
  generatedImages: string[];
  aiMessages: AIMessage[];
  isGenerating: boolean;
  /** Circular buffer of past deck states (max 10). Used for undo/redo. */
  deckHistory: PresentationDeck[];
  /** Index into deckHistory when browsing history. -1 means "at latest state". */
  deckHistoryIndex: number;

  setField: <K extends keyof PresentationFields>(key: K, value: PresentationFields[K]) => void;
  setThemePreset: (name: string) => void;
  setDeck: (deck: PresentationDeck) => void;
  updateCurrentSlide: (partial: Partial<PresentationSlide>) => void;
  updateSlide: (index: number, partial: Partial<PresentationSlide>) => void;
  addSlide: () => void;
  removeSlide: (index: number) => void;
  moveSlide: (from: number, to: number) => void;
  setCurrentSlide: (index: number) => void;
  setLogoUrl: (url: string | null) => void;
  addVenueImage: (url: string) => void;
  removeVenueImage: (index: number) => void;
  setGeneratedImages: (images: string[]) => void;
  /** Add a draggable content-image overlay to the specified slide. */
  addContentImage: (slideIndex: number, image: ContentImage) => void;
  /** Remove a content-image by id from the specified slide. */
  removeContentImage: (slideIndex: number, imageId: string) => void;
  /** Update position/size of a content-image on the specified slide. */
  updateContentImage: (slideIndex: number, imageId: string, partial: Partial<ContentImage>) => void;
  pushAiMessage: (msg: AIMessage) => void;
  setIsGenerating: (b: boolean) => void;
  setEditingId: (id: string | null) => void;
  markSaved: () => void;
  loadFromExisting: (item: PresentationItem) => void;
  reset: () => void;
  /** Snapshot the current deck before a destructive operation (e.g. chat edit). Call this BEFORE applying the change. */
  pushDeckSnapshot: () => void;
  /** Revert the deck to the previous snapshot. */
  undo: () => void;
  /** Re-apply a snapshot that was undone. */
  redo: () => void;
}

export const usePresentationEditorStore = create<PresentationEditorState>((set, get) => ({
  ...DEFAULT_FIELDS,
  editingId: null,
  isDirty: false,
  deck: createInitialDeck(),
  currentSlideIndex: 0,
  logoUrl: null,
  venueImages: [],
  generatedImages: [],
  aiMessages: [],
  isGenerating: false,
  deckHistory: [],
  deckHistoryIndex: -1,

  setField: (key, value) => set({ [key]: value, isDirty: true }),

  setThemePreset: (name) => {
    const preset = THEME_PRESETS.find((p) => p.name === name);
    if (!preset) return;
    set((s) => ({
      theme: name,
      primaryColor: preset.primary,
      accentColor: preset.accent,
      deck: {
        ...s.deck,
        theme: {
          ...(s.deck.theme ?? { font_title: 'Inter', font_body: 'Inter' }),
          primary_color: preset.primary,
          accent_color: preset.accent,
        },
      },
      isDirty: true,
    }));
  },

  setDeck: (deck) => set({ deck, isDirty: true }),

  updateCurrentSlide: (partial) => {
    const { deck, currentSlideIndex } = get();
    const slides = deck.slides.map((sl, i) =>
      i === currentSlideIndex ? { ...sl, ...partial } : sl,
    );
    set({ deck: { ...deck, slides }, isDirty: true });
  },

  updateSlide: (index, partial) => {
    const { deck } = get();
    const slides = deck.slides.map((sl, i) => (i === index ? { ...sl, ...partial } : sl));
    set({ deck: { ...deck, slides }, isDirty: true });
  },

  addSlide: () => {
    const { deck } = get();
    const newSlide = makeBlankSlide('New Slide');
    set({
      deck: { ...deck, slides: [...deck.slides, newSlide] },
      currentSlideIndex: deck.slides.length,
      isDirty: true,
    });
  },

  removeSlide: (index) => {
    const { deck, currentSlideIndex } = get();
    if (deck.slides.length <= 1) return;
    const slides = deck.slides.filter((_, i) => i !== index);
    const nextIndex = Math.min(currentSlideIndex, slides.length - 1);
    set({ deck: { ...deck, slides }, currentSlideIndex: nextIndex, isDirty: true });
  },

  moveSlide: (from, to) => {
    const { deck } = get();
    const slides = [...deck.slides];
    const [moved] = slides.splice(from, 1);
    slides.splice(to, 0, moved);
    set({ deck: { ...deck, slides }, currentSlideIndex: to, isDirty: true });
  },

  setCurrentSlide: (index) => set({ currentSlideIndex: index }),

  setLogoUrl: (url) => set({ logoUrl: url }),

  addVenueImage: (url) => set((s) => ({ venueImages: [...s.venueImages, url] })),
  removeVenueImage: (index) =>
    set((s) => ({ venueImages: s.venueImages.filter((_, i) => i !== index) })),

  setGeneratedImages: (images) => set({ generatedImages: images }),

  addContentImage: (slideIndex, image) => {
    const { deck } = get();
    const slides = deck.slides.map((sl, i) =>
      i === slideIndex ? { ...sl, content_images: [...(sl.content_images ?? []), image] } : sl,
    );
    set({ deck: { ...deck, slides }, isDirty: true });
  },

  removeContentImage: (slideIndex, imageId) => {
    const { deck } = get();
    const slides = deck.slides.map((sl, i) =>
      i === slideIndex
        ? { ...sl, content_images: (sl.content_images ?? []).filter((ci) => ci.id !== imageId) }
        : sl,
    );
    set({ deck: { ...deck, slides }, isDirty: true });
  },

  updateContentImage: (slideIndex, imageId, partial) => {
    const { deck } = get();
    const slides = deck.slides.map((sl, i) =>
      i === slideIndex
        ? {
            ...sl,
            content_images: (sl.content_images ?? []).map((ci) =>
              ci.id === imageId ? { ...ci, ...partial } : ci,
            ),
          }
        : sl,
    );
    set({ deck: { ...deck, slides }, isDirty: true });
  },

  pushAiMessage: (msg) => set((s) => ({ aiMessages: [...s.aiMessages, msg] })),
  setIsGenerating: (b) => set({ isGenerating: b }),
  setEditingId: (id) => set({ editingId: id }),
  markSaved: () => set({ isDirty: false }),

  loadFromExisting: (item) => {
    const ctx = (item.context_data ?? {}) as Record<string, unknown>;
    const deck = item.pptx_code ? parseDeck(item.pptx_code) : createInitialDeck();

    const themeName = typeof ctx.theme === 'string' ? ctx.theme : 'Executive Modern';
    const preset = THEME_PRESETS.find((p) => p.name === themeName) ?? THEME_PRESETS[0];

    set({
      editingId: item.id,
      name: item.name,
      eventType: typeof ctx.eventType === 'string' ? ctx.eventType : 'Corporate Event',
      eventDate: typeof ctx.eventDate === 'string' ? ctx.eventDate : '',
      eventTime: typeof ctx.eventTime === 'string' ? ctx.eventTime : '',
      location: typeof ctx.location === 'string' ? ctx.location : '',
      audience: typeof ctx.audience === 'string' ? ctx.audience : '',
      theme: themeName,
      primaryColor: typeof ctx.primaryColor === 'string' ? ctx.primaryColor : preset.primary,
      accentColor: typeof ctx.accentColor === 'string' ? ctx.accentColor : preset.accent,
      specialNotes: typeof ctx.specialNotes === 'string' ? ctx.specialNotes : '',
      extraSections: '',
      deck,
      generatedImages: item.generated_images ?? [],
      logoUrl:
        item.reference_images?.[0] ?? (typeof ctx.logo_url === 'string' ? ctx.logo_url : null),
      venueImages: item.venue_images ?? [],
      isGenerating: item.status === 'generating',
      isDirty: false,
    });
  },

  pushDeckSnapshot: () => {
    const { deck, deckHistory } = get();
    const snapshot: PresentationDeck = JSON.parse(JSON.stringify(deck));
    const newHistory = [...deckHistory, snapshot].slice(-10);
    set({ deckHistory: newHistory, deckHistoryIndex: -1 });
  },

  undo: () => {
    const { deckHistory, deckHistoryIndex, deck } = get();
    if (deckHistory.length === 0) return;

    let history = [...deckHistory];
    let newIndex = deckHistoryIndex;

    if (newIndex === -1) {
      // Save current live state so redo can return to it, then step back.
      history = [...history, JSON.parse(JSON.stringify(deck))].slice(-11);
      newIndex = history.length - 2;
    } else if (newIndex > 0) {
      newIndex = newIndex - 1;
    } else {
      return; // Already at the oldest snapshot.
    }

    const previousDeck: PresentationDeck = JSON.parse(JSON.stringify(history[newIndex]));
    set({ deck: previousDeck, deckHistory: history, deckHistoryIndex: newIndex, isDirty: true });
  },

  redo: () => {
    const { deckHistory, deckHistoryIndex } = get();
    if (deckHistoryIndex === -1 || deckHistoryIndex >= deckHistory.length - 1) return;

    const newIndex = deckHistoryIndex + 1;
    const nextDeck: PresentationDeck = JSON.parse(JSON.stringify(deckHistory[newIndex]));
    // Returning to the last entry in the buffer means we're back at "latest".
    const resolvedIndex = newIndex === deckHistory.length - 1 ? -1 : newIndex;
    set({ deck: nextDeck, deckHistoryIndex: resolvedIndex, isDirty: true });
  },

  reset: () =>
    set({
      ...DEFAULT_FIELDS,
      editingId: null,
      isDirty: false,
      deck: createInitialDeck(),
      currentSlideIndex: 0,
      logoUrl: null,
      venueImages: [],
      generatedImages: [],
      aiMessages: [],
      isGenerating: false,
      deckHistory: [],
      deckHistoryIndex: -1,
    }),
}));
